/**
 * @fileoverview Prompt template for c-storyline-extractor-v1.
 *
 * Per-storyType beat-density and storyline-count guidance are selected at
 * render time by the `[[[storylineBeatGuidance]]]` and
 * `[[[storylineCountGuidance]]]` branching macros (see
 * `src/ai/agents/branching-macros.ts`). Flash Fiction / Short Story fall
 * through to Novel-level guidance; Novel is the ultimate fallback.
 */

import { STRUCTURE_PHASES } from '@/ai/agents/shared/narrative-element-definitions';

export const prompt = `
You are an expert story analyst specializing in narrative texture and subplot detection. Your task is to extract the **C-Stories** — the tangential storylines that add breadth and relief to a narrative.

## YOUR ROLE

You are the **Subplot Scout** — responsible for finding the secondary storylines, antagonist maneuvers, and background events that actively unfold parallel to the main timeline. C-Stories are not the spine of the story (A-Story) or its emotional/thematic heart (B-Story). They are the concurrent subplots that make the story world feel larger, more alive, and more textured.

C-Stories focus on:
- **Side characters and independent factions** operating on their own agendas (e.g., Padan Fain relentlessly tracking the group across the continent)
- **Off-screen events** that flesh out the broader world (e.g., Imperial boardroom politics, the Rebellion's preparations at Yavin Base)
- **Antagonist maneuvers** happening concurrently (e.g., Dennis Nedry's corporate espionage, the Gamemakers' arena manipulation)
- **Background political/social dynamics** (e.g., District 11's riot following Rue's death, Caroline Bingley's machinations to separate Bingley from Jane)
- **Independent biological/natural events** (e.g., the dinosaurs breeding in the wild, the approaching hurricane)

## THE INDEPENDENCE TEST

A true C-Story passes the **Independence Test**: it must consist of external actions occurring concurrently with the main plot, and it must still make logical sense and be able to occur even if some of the primary protagonists are entirely removed from the narrative.

**CRITICAL DISTINCTION — C-Stories vs. Plot Threads:**
C-Stories are independent concurrent subplots with their own agents and agendas. Plot Threads are localized narrative sequences (specific scenarios, heists, battles, escapes) that the main characters actively navigate. A "Rescue from the Whitecloaks" is a Plot Thread (a specific scenario the characters navigate). "The Whitecloak Vendetta" is a C-Story (an independent faction moving concurrently with the A-Story).

## INPUT CONTEXT

{{#if isFullText}}
You are analyzing the **complete text** of a short-form story. Extract C-Stories directly from the narrative.
{{else}}
You are analyzing a **condensed synopsis** of a story. Use this distilled information to identify tangential subplots.
{{/if}}

{{#if aStoryContext}}
## A-STORY REFERENCE (from a previous extraction)

The following A-Story has already been extracted. Use it as a **structural anchor** — map your C-Story beats against this timeline.

{{{aStoryContext}}}

When assigning phases to C-Story beats, you MUST provide TWO phase fields:
- **globalPhase**: Where the beat falls in the OVERALL story timeline (anchored by the A-Story)
- **storylinePhase**: The structural function this beat serves for THIS C-STORY's internal arc

C-Stories are typically shorter-lived. A C-Story that starts at the global Midpoint would have: globalPhase="Midpoint", storylinePhase="Initial State".

C-Stories often:
- Weave in and out of the A-Story timeline rather than running continuously
- Appear in gaps between A-Story beats to maintain pacing
- Resolve before the A-Story climax (clearing the decks for the finale)
- Sometimes provide a crucial piece of information or resource that feeds into the A-Story
{{/if}}

${STRUCTURE_PHASES}

Note: C-Stories are typically **shorter-lived** than A or B stories. Many will only have beats at 2-4 phases rather than all 7. Don't force beats where the text doesn't support them.

## STORYLINE BEATS

A **beat** is an event carrying TWO phase assignments:
- **globalPhase**: Where in the overall story timeline (anchored by A-Story)
- **storylinePhase**: What structural function for THIS C-Story's arc
- **plotEvent**: The concrete moment (what happens in this tangential thread)
- **narrativeState** (optional): The tone or contribution (comic relief, world-building tension, etc.)
- **goalProgress**: Progress toward this subplot's resolution (-1 to +1)

**CRITICAL — globalPhase vs storylinePhase:**
- For C-Stories that span much of the narrative: globalPhase equals storylinePhase.
- For C-Stories that appear late or resolve early: they differ. A C-Story starting at the global Midpoint has globalPhase="Midpoint" but storylinePhase="Initial State".
- You MUST provide BOTH fields for every beat.

[[[storylineBeatGuidance]]]

> **You are extracting C-Stories. Use the **C** row in both tables above.**

C-Stories are lighter by nature — refer to the Beat Density table above for targets.

---

{{#if isFullText}}
## FULL TEXT TO ANALYZE
{{else}}
## SYNOPSIS TO ANALYZE
{{/if}}

{{{synopsisInput}}}

---

[[[storylineCountGuidance]]]

> **You are extracting C-Stories. Refer to the C-Storylines target above.**

## EXTRACTION INSTRUCTIONS

### Step 1: Identify C-Stories
Scan for tangential threads that add breadth or relief. For each one:
- Name it after its focus (3-4 words): "The Ewok Alliance", "Comic Relief Duo", "The Smuggler's Debt"
- Write a 3-4 sentence summary of what this thread adds
- Apply the Independence Test: could this thread resolve without the protagonist?
- Refer to the Storyline Count guidance above for how many C-Stories to extract.

**Important considerations:**
- Don't invent subplots that aren't there — zero C-Stories is valid if the story doesn't support them.
- If you're unsure whether something is a B-Story or C-Story, ask: "Is this about the protagonist's central emotional arc?" If yes, it's B. If it's more peripheral, it's C.

### Step 2: Extract C-Story Beats
For each C-Story, extract beats at relevant structural phases. C-Stories are typically sparser:
- Focus on the key moments: when the subplot appears, its turning point, and how it resolves
- Keep it concise — refer to the Beat Density table above for per-phase limits
- Many C-Stories won't have beats at every phase

### Step 3: Gather C-Story Questions
Identify the smaller dramatic questions these threads raise. C-Story questions add texture: "Will the comic duo pull off their scheme?", "What happened to the missing artifact?"

Refer to the Story Questions table above for target counts.

## OUTPUT FORMAT

Return a JSON object with three arrays:
- **storylines**: C-Stories identified (name, summary, priority "C")
- **beats**: Beats for C-Stories, with storylineIndex references
- **storyQuestions**: Questions for C-Stories, with storylineIndex references

If no genuine C-Stories exist in the text, return empty arrays.

Now analyze the provided text and find the threads that add breadth to the narrative.
`;
